<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useEquipmentsStore } from '@/store/equipments.js'
import { useCurrency } from '@/composables/useCurrency'
import { useLocation } from '@/composables/useLocation'
import { resolveImageUrl } from '@/composables/useImageUrl'
import SkeletonCard from '@/components/SkeletonCard.vue'
import { debounce } from 'lodash'

const router = useRouter()
const store = useEquipmentsStore()
const { formatPrice } = useCurrency()
const { getDistance } = useLocation()
const getFullImageUrl = resolveImageUrl

// ── Local state ────────────────────────────────────────────────────────────
const searchQuery = ref('')
const isLoading = ref(true)
const allTrending = ref([])

// Rate tier per card
const activeRates = reactive({})
const getActiveRate = (id) => activeRates[id] || 'day'
const setActiveRate = (id, tier) => { activeRates[id] = tier }
const getCurrentRate = (eq) => {
  const tier = getActiveRate(eq.id)
  if (tier === 'week') return eq.weekly_rate ?? eq.hourly_rate * 7 * 0.9
  if (tier === 'month') return eq.monthly_rate ?? eq.hourly_rate * 30 * 0.8
  return eq.hourly_rate
}
const getRatePeriod = (id) => {
  const t = getActiveRate(id)
  return t === 'week' ? 'Week' : t === 'month' ? 'Month' : 'Day'
}

// Owner helpers
const getOwnerName = (eq) => {
  const o = eq.owner
  if (!o) return 'Unknown'
  if (o.first_name || o.last_name) return `${o.first_name || ''} ${o.last_name || ''}`.trim()
  if (o.company_name) return o.company_name
  return 'Unknown'
}
const getOwnerInitial = (eq) => getOwnerName(eq).charAt(0).toUpperCase()
const renderStars = (rating) => {
  const r = rating || 0
  const full = Math.floor(r)
  const half = r % 1 >= 0.5 ? 1 : 0
  return '★'.repeat(full) + (half ? '☆' : '') + '☆'.repeat(5 - full - half)
}

// ── Filtered list based on search ──────────────────────────────────────────
const filteredTrending = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return allTrending.value
  return allTrending.value.filter(e =>
    e.name?.toLowerCase().includes(q) ||
    e.description?.toLowerCase().includes(q) ||
    e.category_name?.toLowerCase().includes(q) ||
    getOwnerName(e).toLowerCase().includes(q) ||
    e.address?.city?.toLowerCase().includes(q)
  )
})

// ── Fetch ──────────────────────────────────────────────────────────────────
const fetchTrending = async () => {
  isLoading.value = true
  try {
    // Fetch a large page to get as many trending items as possible
    const prevSize = store.pageSize
    store.pageSize = 100
    await store.fetchEquipments()
    // Filter for trending
    allTrending.value = store.equipments.filter(e => e.is_trending)
    store.pageSize = prevSize
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchTrending)

// Navigation
const goToDetail = (id) => {
  if (id) router.push({ name: 'equipment-details', params: { id } })
}
</script>

<template>
  <div class="min-h-screen bg-theme-bg">
    <!-- ─── Header ─── -->
    <div class="bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 dark:from-primary-700 dark:via-primary-800 dark:to-neutral-900">
      <div class="max-w-6xl mx-auto px-4 py-10 sm:py-14 text-center text-white relative overflow-hidden">
        <!-- Decorative -->
        <div class="absolute -top-8 -left-8 w-36 h-36 bg-white/5 rounded-full pointer-events-none"></div>
        <div class="absolute -bottom-6 -right-6 w-28 h-28 bg-white/5 rounded-full pointer-events-none"></div>
        <div class="absolute top-1/2 left-1/4 w-2 h-2 bg-white/15 rounded-full pointer-events-none"></div>
        <div class="absolute top-1/3 right-1/3 w-3 h-3 bg-white/10 rounded-full pointer-events-none"></div>

        <div class="relative z-10">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm mb-4">
            <i class="pi pi-bolt text-secondary-400 text-sm"></i>
            <span class="text-sm font-semibold text-white/90">Hot Right Now</span>
          </div>
          <h1 class="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-3">
            Trending Items
          </h1>
          <p class="text-sm sm:text-base text-white/75 max-w-md mx-auto leading-relaxed">
            Discover the most popular items people are renting right now.
          </p>

          <!-- Search bar -->
          <div class="mt-6 max-w-lg mx-auto">
            <div class="relative">
              <i class="pi pi-search absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-sm"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search trending items..."
                class="w-full pl-11 pr-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:bg-white/15 transition"
              />
              <button
                v-if="searchQuery"
                @click="searchQuery = ''"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition p-1"
              >
                <i class="pi pi-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── Content ─── -->
    <div class="max-w-6xl mx-auto px-4 py-8">
      <!-- Breadcrumb -->
      <nav class="flex items-center gap-2 text-sm text-theme-muted mb-6">
        <router-link to="/" class="hover:text-primary-600 transition-colors">Home</router-link>
        <i class="pi pi-angle-right text-xs"></i>
        <span class="text-theme-text font-medium">Trending</span>
      </nav>

      <!-- Results count -->
      <div v-if="!isLoading" class="flex items-center justify-between mb-5">
        <p class="text-sm text-theme-muted">
          <span class="font-semibold text-theme-text">{{ filteredTrending.length }}</span>
          trending item{{ filteredTrending.length !== 1 ? 's' : '' }}
          <span v-if="searchQuery"> matching "<span class="text-primary-600">{{ searchQuery }}</span>"</span>
        </p>
      </div>

      <!-- Skeleton loading -->
      <div v-if="isLoading" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5">
        <SkeletonCard v-for="i in 10" :key="`skel-${i}`" />
      </div>

      <!-- Results grid -->
      <div
        v-else-if="filteredTrending.length > 0"
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5"
        role="list"
        aria-label="Trending items"
      >
        <div
          v-for="equipment in filteredTrending"
          :key="equipment.id"
          class="bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
          role="listitem"
        >
          <!-- Badges -->
          <div v-if="equipment.is_featured || equipment.is_trending" class="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1">
            <span v-if="equipment.is_trending" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-primary-600 text-white shadow">
              <i class="pi pi-bolt text-[9px]"></i> Trending
            </span>
            <span v-if="equipment.is_featured" class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-neutral-900 text-secondary-500 border border-secondary-500 shadow">
              <i class="pi pi-star-fill text-[9px]"></i> Featured
            </span>
          </div>

          <!-- Image -->
          <div @click="goToDetail(equipment.id)" class="relative w-full aspect-[4/3] overflow-hidden bg-theme-bg2">
            <img
              v-if="equipment.images?.length"
              :src="getFullImageUrl(equipment.images[0].image_url)"
              :alt="equipment.name"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            <div v-else class="absolute inset-0 flex items-center justify-center">
              <i class="pi pi-image text-4xl text-theme-muted"></i>
            </div>
            <span
              v-if="!equipment.is_available || equipment.available_quantity === 0"
              class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white"
            >Unavailable</span>
          </div>

          <!-- Content -->
          <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
            <!-- Owner -->
            <div class="flex items-center gap-1 sm:gap-1.5">
              <img
                v-if="equipment.owner?.image_url"
                :src="equipment.owner.image_url"
                :alt="getOwnerName(equipment)"
                class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border"
              />
              <div v-else class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600">
                {{ getOwnerInitial(equipment) }}
              </div>
              <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ getOwnerName(equipment) }}</span>
              <i v-if="equipment.owner?.is_verified" class="pi pi-verified text-xs sm:text-sm text-primary-500" title="Verified"></i>
            </div>

            <!-- Name -->
            <h3 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug">
              {{ store.truncateText(equipment.name, 20) }}
            </h3>

            <!-- Rating + Distance -->
            <div class="flex items-center justify-between text-[11px] sm:text-sm">
              <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
                <span class="text-yellow-500 hidden sm:inline">{{ renderStars(equipment.rating) }}</span>
                <span class="text-yellow-500 sm:hidden">★</span>
                <span class="text-theme-text2 truncate">{{ (equipment.rating || 0).toFixed(1) }} ({{ equipment.equipment_reviews?.length || 0 }})</span>
              </div>
              <span v-if="getDistance(equipment)" class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1">
                <i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>{{ getDistance(equipment) }}
              </span>
            </div>

            <!-- Pricing -->
            <div class="mt-auto">
              <div class="flex items-center gap-1 mb-1">
                <button
                  v-for="tier in [{key:'day',label:'Day'},{key:'week',label:'Week'},{key:'month',label:'Month'}]"
                  :key="tier.key"
                  @click.stop="setActiveRate(equipment.id, tier.key)"
                  class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors"
                  :class="getActiveRate(equipment.id) === tier.key ? 'bg-primary-500 text-white' : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'"
                >{{ tier.label }}</button>
              </div>
              <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">
                {{ formatPrice(getCurrentRate(equipment)) }}
                <span class="text-theme-muted text-[10px] sm:text-xs font-normal">/ {{ getRatePeriod(equipment.id) }}</span>
              </p>
            </div>

            <!-- CTA -->
            <button
              @click="goToDetail(equipment.id)"
              class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600"
              aria-label="Rent this item"
            >View Details</button>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="flex flex-col items-center justify-center py-20 text-center px-4"
      >
        <div class="flex items-center justify-center w-16 h-16 rounded-full bg-theme-bg2 shadow mb-4">
          <i class="pi pi-bolt text-3xl text-theme-muted"></i>
        </div>
        <h2 class="text-xl sm:text-2xl font-semibold text-theme-text2">
          {{ searchQuery ? 'No matching trending items' : 'No trending items right now' }}
        </h2>
        <p class="mt-2 text-sm sm:text-base text-theme-muted max-w-sm">
          {{ searchQuery
            ? 'Try a different search term or clear the filter.'
            : 'Check back soon — trending items update frequently!'
          }}
        </p>
        <div class="flex gap-3 mt-5">
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-theme-border text-theme-text2 font-semibold text-sm hover:border-primary-500 hover:text-primary-600 transition"
          >
            <i class="pi pi-times text-xs"></i> Clear Search
          </button>
          <router-link
            to="/browse"
            class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm transition shadow-sm"
          >
            <i class="pi pi-search text-xs"></i> Browse All Items
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>
